import React, { useEffect, useState, useMemo } from 'react'
import styled from 'styled-components'
import {
  PieChart, Pie, Cell, Legend, Tooltip as PieTooltip,
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as LineTooltip, ResponsiveContainer
} from 'recharts'
import Navbar from '../Navbar'
import Footer from '../Footer'
import IP_URL from '../connect'
import { useSelector } from 'react-redux'
import { selectUser } from '../../Redux/currentUserSlice'
import { toast } from 'react-toastify'
import Papa from 'papaparse'
import jsPDF from 'jspdf'
import 'jspdf-autotable'

const Main = styled.div`
  min-height: 100vh;
  background: #f4f6f8;
`

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 20px 60px;
`

const PageHeader = styled.div`
  margin-bottom: 24px;

  h1 {
    font-size: 24px;
    font-weight: 700;
    color: #1a2b3c;
    margin: 0 0 4px;
  }

  p {
    color: #6b7c8c;
    margin: 0;
    font-size: 14px;
  }
`

const ControlsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 200px;

  label {
    font-size: 12px;
    font-weight: 600;
    color: #6b7c8c;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  select, input {
    padding: 8px 10px;
    border: 1px solid #d8dee4;
    border-radius: 8px;
    font-size: 14px;
    background: #fff;

    &:focus {
      outline: none;
      border-color: #2f80ed;
    }
  }
`

const HelperText = styled.p`
  font-size: 13px;
  color: #8a97a3;
  margin: 4px 0 0;
`

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
`

const ExportGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-shrink: 0;
`

const ExportButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 16px;
  border: 1px solid #d8dee4;
  border-radius: 8px;
  background: ${props => (props.$primary ? '#2f80ed' : '#fff')};
  color: ${props => (props.$primary ? '#fff' : '#2f4356')};
  border-color: ${props => (props.$primary ? '#2f80ed' : '#d8dee4')};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s ease;

  &:hover:not(:disabled) {
    opacity: 0.88;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
`

const StatCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 18px 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  border-left: 4px solid ${props => props.accent || '#2f80ed'};

  h4 {
    margin: 0 0 6px;
    font-size: 12px;
    font-weight: 600;
    color: #8a97a3;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .value {
    font-size: 26px;
    font-weight: 700;
    color: #1a2b3c;
  }

  .unit {
    font-size: 13px;
    font-weight: 500;
    color: #8a97a3;
    margin-left: 4px;
  }
`

const ChartsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`

const ChartCard = styled.div`
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

  h3 {
    margin: 0 0 16px;
    font-size: 15px;
    font-weight: 600;
    color: #1a2b3c;
  }
`

const StateMessage = styled.div`
  padding: 40px;
  text-align: center;
  color: #8a97a3;
`

const NRW_COLOR = '#eb5757'
const DOMESTIC_COLOR = '#2f80ed'

const Waterbalancereport = () => {
  const [bulkMeters, setBulkMeters] = useState([])
  const [bulkLoaded, setBulkLoaded] = useState(false)
  const [selectedBulkMeter, setSelectedBulkMeter] = useState('')
  const [domesticMeters, setDomesticMeters] = useState([])
  const [trendData, setTrendData] = useState([])
  const [totalMeasured, setTotalMeasured] = useState(0)
  const [totalDomesticUsage, setTotalDomesticUsage] = useState(0)
  // Filter modes: 'range' (from/to), 'day' (single day), 'month', 'year'
  const [filterType, setFilterType] = useState('range')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [singleDay, setSingleDay] = useState('')
  const [month, setMonth] = useState('')   // "1".."12" (current year assumed)
  const [year, setYear] = useState('')     // "YYYY"
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const { token, uniqueKey } = useSelector(selectUser);

  // Load list of bulk meters on mount
useEffect(() => {
  if (!uniqueKey) return
  const fetchBulkMeters = async () => {
    try {
      const response = await fetch(`${IP_URL}/waterbalance/bulk/${uniqueKey}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`)
      const data = await response.json()
      setBulkMeters(data)
      if (data.length > 0) setSelectedBulkMeter(data[0].meterId)
    } catch (err) {
      console.error('Failed to fetch bulk meters:', err)
      setError(err.message)
    } finally {
      setBulkLoaded(true)
    }
  }
  fetchBulkMeters()
}, [token, uniqueKey])

  // Translate whichever filter mode is active into explicit UTC bounds so every
  // mode (range/day/month/year) is inclusive and consistent with the others.
  const { from, to, periodLabel } = useMemo(() => {
    const dayStart = d => `${d}T00:00:00.000Z`
    const dayEnd = d => `${d}T23:59:59.999Z`

    if (filterType === 'day') {
      if (!singleDay) return { from: '', to: '', periodLabel: 'All time' }
      return { from: dayStart(singleDay), to: dayEnd(singleDay), periodLabel: singleDay }
    }

    if (filterType === 'month') {
      if (!month) return { from: '', to: '', periodLabel: 'All time' }
      const y = new Date().getUTCFullYear() // current year; user just picks the month
      const m = Number(month)               // 1-12
      const start = new Date(Date.UTC(y, m - 1, 1, 0, 0, 0, 0))
      const end = new Date(Date.UTC(y, m, 0, 23, 59, 59, 999)) // day 0 of next month = last day
      const label = start.toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })
      return { from: start.toISOString(), to: end.toISOString(), periodLabel: label }
    }

    if (filterType === 'year') {
      if (!year) return { from: '', to: '', periodLabel: 'All time' }
      return { from: `${year}-01-01T00:00:00.000Z`, to: `${year}-12-31T23:59:59.999Z`, periodLabel: year }
    }

    // 'range'
    const rFrom = dateFrom ? dayStart(dateFrom) : ''
    const rTo = dateTo ? dayEnd(dateTo) : ''
    let label = 'All time'
    if (dateFrom && dateTo) label = `${dateFrom} to ${dateTo}`
    else if (dateFrom) label = `From ${dateFrom}`
    else if (dateTo) label = `Up to ${dateTo}`
    return { from: rFrom, to: rTo, periodLabel: label }
  }, [filterType, dateFrom, dateTo, singleDay, month, year])

  // Load balance data whenever the selected bulk meter or active period changes
useEffect(() => {
  if (!selectedBulkMeter) return

  const fetchBalanceData = async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams({
        bulkMeterId: selectedBulkMeter,
        ...(from && { from }),
        ...(to && { to })
      })

      const response = await fetch(`${IP_URL}/waterbalance/water-balance/${uniqueKey}?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      if (!response.ok) throw new Error(`Request failed with status ${response.status}`)

      const data = await response.json()
      setDomesticMeters(data.linkedMeters || [])
      setTotalMeasured(data.totalMeasured || 0)
      setTotalDomesticUsage(data.totalDomesticUsage || 0)
      setTrendData(data.trend || [])
    } catch (err) {
      console.error('Failed to fetch water balance data:', err)
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }


  fetchBalanceData()
}, [selectedBulkMeter, from, to, token, uniqueKey])

  const nrw = totalMeasured - totalDomesticUsage
  const nrwPercent = totalMeasured > 0 ? ((nrw / totalMeasured) * 100).toFixed(1) : 0

  const pieData = useMemo(() => ([
    { name: 'Domestic Usage', value: totalDomesticUsage, color: DOMESTIC_COLOR },
    { name: 'NRW', value: Math.max(nrw, 0), color: NRW_COLOR }
  ]), [totalDomesticUsage, nrw])

  const bulkMeterName = useMemo(() => {
    const m = bulkMeters.find(bm => bm.meterId === selectedBulkMeter)
    return m ? (m.meterName || m.serialNumber || m.meterId) : selectedBulkMeter
  }, [bulkMeters, selectedBulkMeter])

  const yearOptions = useMemo(() => {
    const current = new Date().getUTCFullYear()
    return Array.from({ length: 7 }, (_, i) => String(current - i))
  }, [])

  const monthOptions = useMemo(() => (
    Array.from({ length: 12 }, (_, i) => ({
      value: String(i + 1),
      label: new Date(Date.UTC(2000, i, 1)).toLocaleDateString('en-GB', { month: 'long', timeZone: 'UTC' })
    }))
  ), [])

  const hasData = trendData.length > 0 || domesticMeters.length > 0 || totalMeasured > 0

  // Build a filesystem-safe basename, e.g. "water_balance_1-AH1_September-2026"
  const exportBaseName = () =>
    `water_balance_${String(bulkMeterName).replace(/[^\w-]+/g, '-')}_${String(periodLabel).replace(/[^\w-]+/g, '-')}`

  const exportCSV = () => {
    if (!hasData) return toast.warn('No data to export')

    const summary = [
      ['Water Balance Report'],
      ['Bulk Meter', bulkMeterName],
      ['Period', periodLabel],
      ['Total Water Measured (m3)', totalMeasured],
      ['Total Domestic Usage (m3)', totalDomesticUsage],
      ['Non-Revenue Water (m3)', nrw],
      ['NRW %', `${nrwPercent}%`],
    ]

    const breakdown = [
      [],
      ['Linked Meter Breakdown'],
      ['Meter', 'Consumption (m3)'],
      ...domesticMeters.map(m => [m.name || m.meterId, m.consumption]),
    ]

    const trend = [
      [],
      ['Daily Trend'],
      ['Date', 'Measured (m3)', 'Domestic Usage (m3)', 'NRW (m3)'],
      ...trendData.map(t => [t.date, t.measured, t.domesticUsage, t.measured - t.domesticUsage]),
    ]

    const csv = Papa.unparse([...summary, ...breakdown, ...trend])
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `${exportBaseName()}.csv`)
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    toast.success('CSV exported successfully')
  }

  const exportPDF = () => {
    if (!hasData) return toast.warn('No data to export')

    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const generatedAt = new Date().toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
    })

    doc.setFontSize(18)
    doc.setFont(undefined, 'bold')
    doc.text('Water Balance Report', pageWidth / 2, 16, { align: 'center' })

    doc.setFontSize(10)
    doc.setFont(undefined, 'normal')
    doc.setTextColor(100)
    doc.text(`Bulk Meter: ${bulkMeterName}  |  Period: ${periodLabel}`, pageWidth / 2, 23, { align: 'center' })
    doc.text(`Generated on ${generatedAt}`, pageWidth / 2, 28, { align: 'center' })
    doc.setTextColor(0)

    // Summary table
    doc.autoTable({
      head: [['Metric', 'Value']],
      body: [
        ['Total Water Measured', `${totalMeasured.toLocaleString()} m3`],
        ['Total Domestic Usage', `${totalDomesticUsage.toLocaleString()} m3`],
        ['Non-Revenue Water', `${nrw.toLocaleString()} m3 (${nrwPercent}%)`],
      ],
      startY: 34,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [47, 128, 237], textColor: 255, fontStyle: 'bold' },
    })

    // Linked meter breakdown
    if (domesticMeters.length) {
      doc.autoTable({
        head: [['Linked Meter', 'Consumption (m3)']],
        body: domesticMeters.map(m => [m.name || m.meterId, m.consumption.toLocaleString()]),
        startY: doc.lastAutoTable.finalY + 10,
        styles: { fontSize: 9 },
        headStyles: { fillColor: [39, 174, 96], textColor: 255, fontStyle: 'bold' },
      })
    }

    // Daily trend
    if (trendData.length) {
      doc.autoTable({
        head: [['Date', 'Measured (m3)', 'Domestic Usage (m3)', 'NRW (m3)']],
        body: trendData.map(t => [
          t.date,
          t.measured.toLocaleString(),
          t.domesticUsage.toLocaleString(),
          (t.measured - t.domesticUsage).toLocaleString(),
        ]),
        startY: doc.lastAutoTable.finalY + 10,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [47, 128, 237], textColor: 255, fontStyle: 'bold' },
      })
    }

    doc.save(`${exportBaseName()}.pdf`)
    toast.success('PDF exported successfully')
  }

  return (
    <Main>
      <Navbar />
      <Container>
        <HeaderRow>
          <PageHeader>
            <h1>Water Balance Report</h1>
            <p>Compare bulk meter supply against domestic consumption to track Non-Revenue Water (NRW).</p>
          </PageHeader>
          <ExportGroup>
            <ExportButton onClick={exportCSV} disabled={loading || !hasData}>
              ⭳ CSV
            </ExportButton>
            <ExportButton $primary onClick={exportPDF} disabled={loading || !hasData}>
              ⭳ PDF
            </ExportButton>
          </ExportGroup>
        </HeaderRow>

        <ControlsBar>
          <Field>
            <label>Bulk Meter</label>
            <select
              value={selectedBulkMeter}
              onChange={e => setSelectedBulkMeter(e.target.value)}
            >
              {bulkMeters.length === 0 && <option value="">No bulk meters found</option>}
              {bulkMeters.map(meter => (
                <option key={meter.meterId} value={meter.meterId}>
                  {meter.meterName || meter.serialNumber || meter.meterId}
                </option>
              ))}
            </select>
            <HelperText>
              Automatically includes all meters linked downstream of this bulk meter ({domesticMeters.length} linked)
            </HelperText>
          </Field>

          <Field>
            <label>Filter By</label>
            <select value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="range">Date Range</option>
              <option value="day">Single Day</option>
              <option value="month">Monthly</option>
              <option value="year">Yearly</option>
            </select>
          </Field>

          {filterType === 'range' && (
            <>
              <Field>
                <label>From</label>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
              </Field>
              <Field>
                <label>To</label>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
              </Field>
            </>
          )}

          {filterType === 'day' && (
            <Field>
              <label>Day</label>
              <input type="date" value={singleDay} onChange={e => setSingleDay(e.target.value)} />
            </Field>
          )}

          {filterType === 'month' && (
            <Field>
              <label>Month</label>
              <select value={month} onChange={e => setMonth(e.target.value)}>
                <option value="">Select month</option>
                {monthOptions.map(mo => (
                  <option key={mo.value} value={mo.value}>{mo.label}</option>
                ))}
              </select>
            </Field>
          )}

          {filterType === 'year' && (
            <Field>
              <label>Year</label>
              <select value={year} onChange={e => setYear(e.target.value)}>
                <option value="">Select year</option>
                {yearOptions.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </Field>
          )}
        </ControlsBar>

        {error && <StateMessage>Error: {error}</StateMessage>}
        {!error && bulkLoaded && bulkMeters.length === 0 && (
          <StateMessage>No commercial (bulk) meters found for this organization.</StateMessage>
        )}
        {loading && <StateMessage>Loading water balance data...</StateMessage>}

        {!loading && !error && bulkMeters.length > 0 && (
          <>
            <StatGrid>
              <StatCard accent="#2f80ed">
                <h4>Total Water Measured</h4>
                <span className="value">{totalMeasured.toLocaleString()}</span>
                <span className="unit">m³</span>
              </StatCard>
              <StatCard accent="#27ae60">
                <h4>Total Domestic Usage</h4>
                <span className="value">{totalDomesticUsage.toLocaleString()}</span>
                <span className="unit">m³</span>
              </StatCard>
              <StatCard accent="#eb5757">
                <h4>Non-Revenue Water</h4>
                <span className="value">{nrw.toLocaleString()}</span>
                <span className="unit">m³ ({nrwPercent}%)</span>
              </StatCard>
            </StatGrid>

            <ChartsGrid>
              <ChartCard>
                <h3>Water Balance Split</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={60}
                      outerRadius={95}
                      paddingAngle={2}
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <PieTooltip formatter={(value) => `${value.toLocaleString()} m³`} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard>
                <h3>Measured vs Domestic Usage Trend</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" />
                    <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <LineTooltip formatter={(value) => `${value.toLocaleString()} m³`} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="measured"
                      name="Measured"
                      stroke={DOMESTIC_COLOR}
                      strokeWidth={2}
                      dot={false}
                    />
                    <Line
                      type="monotone"
                      dataKey="domesticUsage"
                      name="Domestic Usage"
                      stroke="#27ae60"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </ChartCard>
            </ChartsGrid>
          </>
        )}
      </Container>
      <Footer />
    </Main>
  )
}

export default Waterbalancereport